import { useState, useEffect } from "react";
import {
  PaymentElement,
  useStripe,
  useElements,
  AddressElement,
} from "@stripe/react-stripe-js";
import type { PaymentElementProps } from "@stripe/react-stripe-js";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useSession } from "@/services/auth0/session";
import { Loader2 } from "lucide-react";
import axios from "axios";
import { logger } from "@/services/logger";

interface SetupIntentResponse {
  clientSecret: string;
}

export function PaymentMethodForm() {
  const [clientSecret, setClientSecret] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const { customer, token, user } = useSession();

  useEffect(() => {
    const initializeSetupIntent = async () => {
      if (!token || !customer?.billing?.stripeCustomerId) return;
      try {
        const response = await axios.post<SetupIntentResponse>(
          `${import.meta.env.VITE_API_URL}/api/v1/stripe/setup-intent`,
          { stripeCustomerId: customer.billing.stripeCustomerId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        setClientSecret(response.data.clientSecret);
      } catch (error) {
        logger.error("Error initializing payment form:", error);
        toast({
          variant: "destructive",
          title: "Error initializing payment form",
          description: "Please try again later",
        });
      }
    };

    void initializeSetupIntent();
  }, [token, customer?.billing?.stripeCustomerId]);

  const handleSubmit = async (event: React.FormEvent) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      // Stripe.js is not ready yet.
      // Make sure to disable form submission until Stripe.js is ready.
      toast({
        variant: "destructive",
        title: "Unable to process payment",
        description: "Please try again later",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message);
      }

      const result = await stripe.confirmSetup({
        elements,
        clientSecret,
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: {
            billing_details: {
              name: user?.firstName ? `${user.firstName} ${user.lastName}` : "",
              phone: user?.phone ?? "",
              email: user?.email ?? "",
              address: {
                line1: customer?.billing?.address?.line1 ?? "",
                line2: customer?.billing?.address?.line2 ?? "",
                city: customer?.billing?.address?.city ?? "",
                state: customer?.billing?.address?.province ?? "",
                postal_code: customer?.billing?.address?.postalCode ?? "",
                country: customer?.billing?.address?.country ?? "CA",
              },
            },
          },
        },
      });

      if (result.error) {
        toast({
          variant: "destructive",
          title: "Error adding payment method",
          description: result.error.message ?? "An unknown error occurred",
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error adding payment method",
        description:
          error instanceof Error
            ? error.message
            : "Please try refreshing the page and try again",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!clientSecret) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const paymentElementOptions: PaymentElementProps["options"] = {
    layout: "tabs",
    business: {
      name: "TooEZ Inc.",
    },
    defaultValues: {
      billingDetails: {
        name: user?.firstName ? `${user.firstName} ${user.lastName}` : "",
        phone: user?.phone ?? "",
      },
    },
    fields: {
      billingDetails: "auto",
    },
    wallets: {
      applePay: "auto",
      googlePay: "auto",
    },
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <AddressElement
        options={{
          mode: "billing",
          fields: {
            phone: "always",
          },
          display: {
            name: "full",
          },
          validation: {
            phone: {
              required: "always",
            },
          },
          defaultValues: {
            name: user?.firstName ? `${user.firstName} ${user.lastName}` : "",
            phone: user?.phone,
            address: {
              line1: customer?.billing?.address?.line1 ?? "",
              line2: customer?.billing?.address?.line2 ?? "",
              city: customer?.billing?.address?.city ?? "",
              state: customer?.billing?.address?.province ?? "",
              postal_code: customer?.billing?.address?.postalCode ?? "",
              country: customer?.billing?.address?.country ?? "CA",
            },
          },
          autocomplete: {
            mode: "automatic",
          },
        }}
      />
      <PaymentElement options={paymentElementOptions} />
      <Button type="submit" disabled={!stripe || isLoading} className="w-full">
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Adding...
          </>
        ) : (
          "Add Payment Method"
        )}
      </Button>
    </form>
  );
}
